import {
  VStack,
  HStack,
  Text,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  Input,
} from '@chakra-ui/react';
import { useState, useEffect, useRef } from 'react';
import OtpInput from 'react-otp-input';
import { useMfa } from '../../../context/MfaContext';
import { useMfaApi } from '../../../hooks/useMfaApi';
import { useMfaToast } from '../../../hooks/useMfaToast';
import { useMfaNavigation } from '../../../hooks/useMfaNavigation';

function MfaEnterCode({
  onSuccess,
  isEmail = false,
  isModal = false,
  isRecoveryCode = false,
  useAnotherMethod = false,
}) {
  const [otp, setOtp] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const { isSettingUp } = useMfa();
  const [error, setError] = useState(false);
  const [recoveryCodeError, setRecoveryCodeError] = useState(false);
  const { verifyOtp, sendEmailCode, verifyRecoveryCode } = useMfaApi();
  const { showSuccessToast, showErrorToast } = useMfaToast();
  const {
    handleSetupSuccess,
    handleVerificationSuccess,
    handleTooManyAttempts,
  } = useMfaNavigation();

  const inputRefs = useRef({});
  const prevOtpLenRef = useRef(0);
  const focusIndex = (index) => {
    const input = inputRefs.current[index];
    if (input && typeof input.focus === 'function') input.focus();
  };

  const resendCode = async () => {
    try {
      const response = await sendEmailCode();
      if (response.status === 200) {
        showSuccessToast(
          response.data.message,
          "We've sent a new code. Please check your inbox and if you don't see it, check your spam folder."
        );
      }
    } catch (error) {
      showErrorToast(
        'Error sending OTP code email',
        error.response?.data?.message || 'Failed to send email code'
      );
    }
  };

  const handleOtpAttempt = async () => {
    try {
      const response = await verifyOtp(otp);
      if (response.status === 200 && isSettingUp) {
        handleSetupSuccess(onSuccess);
      } else if (response.status === 200 && !isSettingUp) {
        handleVerificationSuccess(onSuccess);
      }
    } catch (error) {
      if (error.status === 401) {
        setOtp('');
        setError(true);
      } else if (error.status === 423) {
        setError(false);
        handleTooManyAttempts(error);
      }
    }
  };

  const handleRecoveryCodeAttempt = async () => {
    try {
      const response = await verifyRecoveryCode(recoveryCode);
      if (response.status === 200) {
        handleVerificationSuccess(onSuccess);
      }
    } catch (error) {
      if (error.status === 401) {
        setRecoveryCode('');
        setRecoveryCodeError(true);
      } else if (error.status === 423) {
        setRecoveryCodeError(false);
        handleTooManyAttempts(error);
      }
    }
  };

  useEffect(() => {
    if (otp.length !== 0) {
      setError(false);
    }
    if (prevOtpLenRef.current > 0 && otp.length === 0) {
      focusIndex(0);
    }
    prevOtpLenRef.current = otp.length;
  }, [otp]);

  useEffect(() => {
    if (recoveryCode.length !== 0) {
      setRecoveryCodeError(false);
    }
  }, [recoveryCode]);

  return (
    <VStack spacing={4} alignItems="flex-start" w="full">
      <VStack mb={4} alignItems="flex-start">
        <Text textStyle="heading4">
          {isRecoveryCode
            ? 'Enter your recovery code'
            : 'Enter your one time code'}
        </Text>
        {isRecoveryCode ? (
          <Input
            id="recoveryCode"
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && recoveryCode.length > 0) {
                handleRecoveryCodeAttempt();
              }
            }}
            w="400px"
            _focus={{
              borderColor: 'brand',
            }}
            _hover={{
              borderColor: 'brand',
            }}
          />
        ) : (
          <OtpInput
            value={otp}
            onChange={(otp) => setOtp(otp)}
            numInputs={6}
            isInputNum={true}
            shouldAutoFocus={true}
            renderSeparator={<span style={{ width: '8px' }}></span>}
            renderInput={(inputProps, index) => (
              <input
                {...inputProps}
                ref={(ref) => {
                  if (typeof inputProps.ref === 'function') inputProps.ref(ref);
                  else if (inputProps.ref) inputProps.ref.current = ref;
                  inputRefs.current[index] = ref;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && otp.length === 6) {
                    handleOtpAttempt();
                  }
                }}
                style={{
                  backgroundColor: isModal ? '#FFFFFF' : '#F8F8F8',
                  borderBottom: '2px solid #D9D9D9',
                  width: '54px',
                  height: '54px',
                  fontSize: '20px',
                  fontWeight: '400',
                  textAlign: 'center',
                }}
              />
            )}
          />
        )}
        {(error || recoveryCodeError) && (
          <Alert status="error" backgroundColor="transparent" px={0}>
            <AlertIcon boxSize="16px" color="red" />
            <AlertDescription textStyle="bodySmall" color="red">
              Invalid or Expired Code. Please try again.
            </AlertDescription>
          </Alert>
        )}
      </VStack>
      <Button
        w="full"
        mt={4}
        isDisabled={
          isRecoveryCode ? recoveryCode.length === 0 : otp.length !== 6
        }
        onClick={isRecoveryCode ? handleRecoveryCodeAttempt : handleOtpAttempt}
      >
        Continue
      </Button>
      {(isEmail || useAnotherMethod) && (
        <HStack w="full" alignItems="center" justifyContent="flex-start" mt={4}>
          <Text textStyle="bodySmall" color="#666666">
            Didn't receive the code?
          </Text>
          <Text
            textStyle="bodySmall"
            color="#666666"
            textDecoration="underline"
            cursor="pointer"
            onClick={resendCode}
            _hover={{
              color: 'orangePrimary',
            }}
          >
            Resend
          </Text>
        </HStack>
      )}
    </VStack>
  );
}

export default MfaEnterCode;
